package com.eladfinish.bible.contest.utils

import com.eladfinish.bible.contest.analytics.crashlytics.AppCrashlytics
import com.eladfinish.bible.contest.extensions.replaceAllWithFunction
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.splitByRegex
import io.github.aakira.napier.Napier

/**
 * Developed by
 * @author Elad Finish
 */

// put a question mark after each Vav and Yud letters to be ignored if need for missing spelling
fun String.makeVavAndYudOptional() =
    replaceRegex(regex = "\\B([וי])\\B", replacement = "$1?")


//fun gematriaWord(word: String?): Int =
//        word?.fold(0) { acc, ot -> acc + gematriaOt(ot) } ?: 0

fun String?.gematriaWord(): Int =
    this?.sumOf { gematriaOt(it) } ?: 0

private fun gematriaOt(ot: Char): Int =
    when (ot) {
        'א' -> 1
        'ב' -> 2
        'ג' -> 3
        'ד' -> 4
        'ה' -> 5
        'ו' -> 6
        'ז' -> 7
        'ח' -> 8
        'ט' -> 9
        'י' -> 10
        'כ', 'ך' -> 20
        'ל' -> 30
        'מ', 'ם' -> 40
        'נ', 'ן' -> 50
        'ס' -> 60
        'ע' -> 70
        'פ', 'ף' -> 80
        'צ', 'ץ' -> 90
        'ק' -> 100
        'ר' -> 200
        'ש' -> 300
        'ת' -> 400
        else -> 0
    }


private val SOFIT_TO_REGULAR = mapOf(
    'ך' to 'כ',
    'ם' to 'מ',
    'ן' to 'נ',
    'ף' to 'פ',
    'ץ' to 'צ',
)

// Replaces a word-final otiot sofiot letter (ךםןףץ) with its regular form,
// e.g. "שמן" -> "שמנ", so an answer ending in a final letter can also be
// matched against the equivalent regular letter.
fun String.replaceFinalLetterWithRegular(): String {
    if (isEmpty()) return this
    val replacement = SOFIT_TO_REGULAR[last()] ?: return this
    return dropLast(1) + replacement
}

// Applies replaceFinalLetterWithRegular() to each word of a (possibly multi-word,
// space-separated) answer and joins the result without spaces, e.g. "בית לחם" -> "ביתלחמ".
fun String.wordsWithFinalLettersReplacedByRegularNoSpaces(): String =
    split(" ").joinToString(separator = "") { it.replaceFinalLetterWithRegular() }

fun convertWordsToNumber(input: String): String {
    var str = input
    val text = str

    val pairList = listOf(
//            "עֶשְׂרִים אֶלֶף" to "20000",
        "אֶלֶף|אָלֶף" to "1000",
        "אַלְפַּיִם" to "2000",
        "שְׁלֹשֶׁת אֲלָפִים" to "3000",
        "אַרְבַּעַת אֲלָפִים" to "4000",
        "חֲמֵשֶׁת אֲלָפִים" to "5000",
        "שֵׁשֶׁת אֲלָפִים" to "6000",
        "שִׁבְעַת אֲלָפִים" to "7000",
        "שְׁמֹנַת אֲלָפִים" to "8000",
        "תִשְׁעַת אֲלָפִים" to "9000",
        "עֲשֶׂרֶת אֲלָפִים|עֲשֶׂרֶת אַלְפֵי" to "10000",
        "רִבֹּו|רְבָבָה" to "10000",
        "מָאתַיִם" to "200",
//            "מֵאֹות|מֵאוֹת" to "מאות",
        "מֵאֹ?וֹ?ת|מֵאֹו?ת" to "מאות",
        "שְׁלֹשׁ מאות" to "300",
        "אַרְבַּע מאות" to "400",
        "חֲמֵשׁ מאות" to "500",
        "שֵׁשׁ מאות" to "600",
//            "שְׁבַע מֵאֹ?וֹ?ת" to "700",
        "שְׁבַע מאות" to "700",
        "שְׁמֹנֶה מאות" to "800",
        "תּ?ְשַׁע מאות" to "900",
        "עֶשְׂרֵה" to "+10",
        "עָשָׂר" to "+10",
        "עֲשָׂרָה|עֲשֶׂרֶת|עֶשֶׂר|עָשֶׂר|עֲשַׂר|עֶשְׂרֵה|(הֶ)?עָשָׂר" to "10",
        "עֶשְׂרִים" to "20",
        "שְׁלֹשִׁים" to "30",
        "אַרְבָּעִים" to "40",
        "חֲמִשִּׁים" to "50",
        "שִׁשִּׁים" to "60",
        "שִׁבְעִים" to "70",
        "שְׁמֹו?נִים" to "80", //שְׁמֹונִים|שְׁמֹנִים
        "תּ?ִשְׁעִים" to "90",
        "מְאָה|מֵאָה|מֵּאָה|מְאַת" to "100",
        "אֶחָד|אַחַד|אַחַת|אֶחָת|עַשְׁתֵּי" to "1",
        "שְׁנֵים|שְׁתֵּים|שּׁ?ְנֵי|שּׁ?ְתֵּי|שְׁנַיִם|שְׁתַּיִם" to "2",
//            "שְׁ[נת]ֵים|שּׁ?ְ[נת]ֵי|שְׁנַיִם|שְׁתַּיִם" to "2",
//            "שּׁ?ְתֵי|שּׁ?ְנֵי|שְׁנַיִם|שְׁתַּיִם" to "2",
//            "שְׁנֵי|שְׁתֵּי|שְׁנַיִם|שְׁתַּיִם" to "2",
        "שָׁלֹו?שׁ|שְׁלֹשָׁה|שְׁלֹשֶׁת|תְּלָתָה|שְׁלֹשׁ" to "3",
        "אַרְבַּע|אַרְבָּעָה|אַרְבַּעַת" to "4",
        "חֲמִשָּׁה|חֲמֵשֶׁת|ח[ֲָ]מֵשׁ" to "5",
//           "חֲמִשָּׁה|חֲמֵשֶׁת|חֲמֵשׁ" to "5",
        "שִׁשָּׁה|שֵׁשֶׁת|שֵׁשׁ" to "6",
        "שֶׁבַע|שְׁבַע|שִׁבְעָה|שִׁבְעַת" to "7",
        "שְׁמֹו?נָה|שְׁמֹנֶה|שְׁמֹנַת" to "8",
        "תּ?ִשְׁעָה|תּ?ִשְׁעַת|תּ?ֵשַׁע|תּ?ְשַׁע" to "9",
        "ו" + "\\D?(?=\\d)" to "+",
        "\\s?\\+\\s?" to "+",
    )

    pairList.forEach {
        str = str.replaceRegex(regex = it.first, replacement = it.second)
    }

    val afterAddition = str.replaceAllWithFunction(
        "\\+?(\\d+\\+)+\\d+"
    ) {
        val numbers = it.splitByRegex("\\+")
        var sum = 0
        for (addend in numbers) {
            if (addend.isNotEmpty()) {
                sum += addend.toInt()
            }
        }
        sum.toString()
    }
    val afterMultiplication = afterAddition.replaceAllWithFunction(
        "(\\d+) (1000)"
    ) {
        val numbers = it.splitByRegex("\\s")
        var product = 0
        try {
            product = numbers[0].toInt() * numbers[1].toInt()
        } catch (e: NumberFormatException) {
            AppCrashlytics.logException(e)
        }
        product.toString()
    }
    val afterAddition2 = afterMultiplication.replaceAllWithFunction(
        "(\\d+) (\\d+)"
    ) {
        val numbers = it.splitByRegex("\\s")
        var sum = 0
        try {
            sum = numbers[0].toInt() + numbers[1].toInt()
        } catch (e: NumberFormatException) {
            AppCrashlytics.logException(e)
        }
        sum.toString()
    }
    val output = afterAddition2.replaceRegex(regex = "בְּ", replacement = "ב")
    val fullOutput = (text + " ~> " + str + " [+]-> " + afterAddition
            + " [*]-> " + afterMultiplication
            + " [+]-> " + output)

//        str += " [+]-> " + afterAddition
//                + " [*]-> " + afterMultiplication
//                + " [+]-> " + afterAddition2
//
//        str = text + " ~> " + str
    Napier.d("fullOutput words as a number: $fullOutput")
    return output
}
